import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, Button, Collapse, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Chip, Divider, CircularProgress,
  Alert, Avatar
} from '@mui/material';
import {
  AutoAwesome, Close, ExpandMore, ExpandLess, Refresh,
  Assignment, TrendingUp, Lightbulb, CheckCircle, Warning, Schedule,
  Info, AttachMoney, Speed, Star, Work, Security
} from '@mui/icons-material';
import api from '../../services/api';
import { status as statusColors } from '../../theme/colors';

/**
 * Verifier Smart Insights
 * Provides intelligent insights and recommendations for verifiers
 * Matches the Smart Insights design from Contractor Dashboard
 */
const VerifierSmartAssistant = ({ profile, stats, assignments, aiInsights, onDismiss, onRefresh }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we have AI insights from backend, use them; otherwise generate fallback
    if (aiInsights && aiInsights.length > 0) {
      // Transform backend insights to component format
      const transformedInsights = aiInsights.map(insight => ({
        type: insight.type || 'info',
        category: insight.category || 'suggestion',
        icon: insight.icon || 'info',
        title: insight.title,
        description: insight.description,
        actionText: insight.actionText,
        actionRoute: insight.actionRoute,
        priority: insight.priority || 'medium',
        data: insight.data || {}
      }));
      setInsights(transformedInsights);
    } else {
      generateInsights();
    }
  }, [profile, stats, assignments, aiInsights]);

  // Get icon for insight - matching Contractor design
  const getInsightIcon = (iconName, category, type) => {
    const iconProps = { sx: { fontSize: 24 } };
    
    if (iconName === 'assignment' || iconName === 'work') return <Assignment {...iconProps} sx={{ color: statusColors.info }} />;
    if (iconName === 'speed') return <Speed {...iconProps} sx={{ color: '#43B581' }} />;
    if (iconName === 'money' || iconName === 'trending_up') return <AttachMoney {...iconProps} sx={{ color: '#FAA61A' }} />;
    if (iconName === 'star') return <Star {...iconProps} sx={{ color: '#FAA61A' }} />;
    if (iconName === 'person') return <Lightbulb {...iconProps} sx={{ color: statusColors.info }} />;
    if (iconName === 'check_circle') return <CheckCircle {...iconProps} sx={{ color: '#43B581' }} />;
    
    if (category === 'urgent' || iconName === 'warning') return <Warning {...iconProps} sx={{ color: '#F04747' }} />;
    if (category === 'important') return <Info {...iconProps} sx={{ color: '#FAA61A' }} />;
    if (type === 'fraud_alert' || type === 'urgent') return <Security {...iconProps} sx={{ color: '#F04747' }} />;
    if (type.includes('prediction') || type.includes('forecast')) return <TrendingUp {...iconProps} sx={{ color: statusColors.info }} />;
    
    return <CheckCircle {...iconProps} sx={{ color: '#43B581' }} />;
  };

  // Get color for insight - matching Contractor design
  const getInsightColor = (category, type) => {
    if (category === 'urgent' || type === 'fraud_alert' || type === 'urgent') return '#F04747';
    if (category === 'important') return '#FAA61A';
    return statusColors.info;
  };

  // Track user interaction
  const trackInteraction = async (insight, action) => {
    try {
      await api.post('/smart-insights/track', {
        insightType: insight.type,
        insightCategory: insight.category,
        assistantType: 'verifier_dashboard',
        action: action,
        insightData: insight.data
      }).catch(() => {
        // Silently fail tracking
      });
    } catch (err) {
      // Silently fail tracking
    }
  };

  // Handle insight click
  const handleInsightClick = (insight) => {
    trackInteraction(insight, 'clicked');
    if (insight.actionRoute) {
      navigate(insight.actionRoute);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    // Track dismissal for each visible insight
    insights.forEach(insight => trackInteraction(insight, 'dismissed'));
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Group insights by category - matching Contractor design
  const groupedInsights = {
    urgent: insights.filter(i => i.category === 'urgent'),
    important: insights.filter(i => i.category === 'important'),
    suggestion: insights.filter(i => i.category === 'suggestion' || i.category === 'performance' || i.category === 'info')
  };

  const totalInsights = insights.length;

  const generateInsights = () => {
    const newInsights = [];

    // Available assignments insight
    if (assignments?.available > 0) {
      newInsights.push({
        type: 'opportunity',
        category: 'important',
        icon: 'assignment',
        title: `${assignments.available} new assignments available`,
        description: 'Check the marketplace for verification opportunities in your area',
        actionText: 'View Marketplace',
        actionRoute: '/verifier/marketplace',
        priority: 'high'
      });
    }

    // Performance insight - only show if user has at least 5 assignments
    if (stats?.completionRate < 80 && (stats?.totalVerifications >= 5 || stats?.total_verifications >= 5)) {
      newInsights.push({
        type: 'warning',
        category: 'urgent',
        icon: 'warning',
        title: 'Completion rate below target',
        description: `Your completion rate is ${stats.completionRate}%. Aim for 85%+ to maintain professional status.`,
        actionText: 'View Tips',
        actionRoute: '/verifier/help',
        priority: 'high'
      });
    } else if (stats?.completionRate >= 95 && (stats?.totalVerifications >= 5 || stats?.total_verifications >= 5)) {
      newInsights.push({
        type: 'success',
        category: 'suggestion',
        icon: 'check_circle',
        title: 'Excellent completion rate!',
        description: `You're maintaining a ${stats.completionRate}% completion rate. Keep up the great work!`,
        priority: 'low'
      });
    }

    // Rating insight
    if (stats?.averageRating >= 4.5) {
      newInsights.push({
        type: 'success',
        category: 'suggestion',
        icon: 'star',
        title: 'Outstanding ratings',
        description: `Your ${stats.averageRating.toFixed(1)} star rating is excellent. Clients trust your work!`,
        priority: 'low'
      });
    } else if (stats?.averageRating < 4.0 && stats?.totalReviews > 5) {
      newInsights.push({
        type: 'warning',
        category: 'urgent',
        icon: 'warning',
        title: 'Rating needs attention',
        description: 'Focus on quality and communication to improve your rating',
        actionText: 'View Feedback',
        actionRoute: '/verifier/profile',
        priority: 'high'
      });
    }

    // Response time insight
    if (stats?.averageResponseTime > 24) {
      newInsights.push({
        type: 'tip',
        category: 'suggestion',
        icon: 'speed',
        title: 'Faster response = more assignments',
        description: 'Try to respond to assignments within 12 hours to increase your booking rate',
        actionText: null,
        actionRoute: null,
        priority: 'medium'
      });
    }

    // Earnings forecast
    if (stats?.earningsThisMonth > 0) {
      const projectedMonthly = (stats.earningsThisMonth / new Date().getDate()) * 30;
      newInsights.push({
        type: 'info',
        category: 'suggestion',
        icon: 'trending_up',
        title: 'Earnings forecast',
        description: `At current pace, you're projected to earn $${Math.round(projectedMonthly).toLocaleString()} this month`,
        actionText: null,
        actionRoute: null,
        priority: 'low'
      });
    }

    setInsights(newInsights.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }));
  };

  if (dismissed) {
    return null;
  }

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          mb: 3,
          border: '2px solid #43B581',
          bgcolor: '#43B58110',
          boxShadow: '0 4px 12px rgba(67, 181, 129, 0.15)'
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={30} sx={{ color: '#43B581' }} />
          <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
            Analyzing opportunities for you...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (totalInsights === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
        border: '2px solid #43B581',
        bgcolor: '#43B58110',
        boxShadow: '0 4px 12px rgba(67, 181, 129, 0.15)',
        overflow: 'visible'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoAwesome sx={{ color: '#43B581', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Smart Insights
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Personalized insights and recommendations
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${totalInsights} insight${totalInsights > 1 ? 's' : ''}`}
              size="small"
              sx={{
                bgcolor: '#43B581',
                color: 'white',
                fontWeight: 600,
                height: 24
              }}
            />
            {onRefresh && (
              <IconButton onClick={onRefresh} size="small" title="Refresh insights" disabled={loading}>
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {onDismiss && (
              <IconButton onClick={handleDismiss} size="small">
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box>
            {/* Urgent Insights */}
            {groupedInsights.urgent.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#F04747',
                    fontWeight: 600,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Warning sx={{ fontSize: 18 }} />
                  Urgent
                </Typography>
                <List sx={{ p: 0 }}>
                  {groupedInsights.urgent.map((insight, index) => (
                    <ListItem
                      key={index}
                      onClick={() => handleInsightClick(insight)}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        mb: 1,
                        border: `1.5px solid ${getInsightColor(insight.category, insight.type)}`,
                        cursor: insight.actionRoute ? 'pointer' : 'default',
                        transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': insight.actionRoute ? {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        } : {},
                        p: 2
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: `${getInsightColor(insight.category, insight.type)}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInsightIcon(insight.icon, insight.category, insight.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {insight.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {insight.description}
                          </Typography>
                        }
                      />
                      {insight.actionText && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: getInsightColor(insight.category, insight.type),
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                            '&:hover': {
                              bgcolor: getInsightColor(insight.category, insight.type),
                              filter: 'brightness(0.9)'
                            }
                          }}
                        >
                          {insight.actionText}
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Important Insights */}
            {groupedInsights.important.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#FAA61A',
                    fontWeight: 600,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Info sx={{ fontSize: 18 }} />
                  Important
                </Typography>
                <List sx={{ p: 0 }}>
                  {groupedInsights.important.map((insight, index) => (
                    <ListItem
                      key={index}
                      onClick={() => handleInsightClick(insight)}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        mb: 1,
                        border: `1.5px solid ${getInsightColor(insight.category, insight.type)}`,
                        cursor: insight.actionRoute ? 'pointer' : 'default',
                        transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': insight.actionRoute ? {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        } : {},
                        p: 2
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: `${getInsightColor(insight.category, insight.type)}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInsightIcon(insight.icon, insight.category, insight.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {insight.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {insight.description}
                          </Typography>
                        }
                      />
                      {insight.actionText && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: getInsightColor(insight.category, insight.type),
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                            '&:hover': {
                              bgcolor: getInsightColor(insight.category, insight.type),
                              filter: 'brightness(0.9)'
                            }
                          }}
                        >
                          {insight.actionText}
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Suggestions */}
            {groupedInsights.suggestion.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: statusColors.info,
                    fontWeight: 600,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Lightbulb sx={{ fontSize: 18 }} />
                  Suggestions
                </Typography>
                <List sx={{ p: 0 }}>
                  {groupedInsights.suggestion.map((insight, index) => (
                    <ListItem
                      key={index}
                      onClick={() => handleInsightClick(insight)}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        mb: index < groupedInsights.suggestion.length - 1 ? 1 : 0,
                        border: `1.5px solid ${getInsightColor(insight.category, insight.type)}`,
                        cursor: insight.actionRoute ? 'pointer' : 'default',
                        transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': insight.actionRoute ? {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        } : {},
                        p: 2
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: `${getInsightColor(insight.category, insight.type)}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInsightIcon(insight.icon, insight.category, insight.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {insight.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {insight.description}
                          </Typography>
                        }
                      />
                      {insight.actionText && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: getInsightColor(insight.category, insight.type),
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                            '&:hover': {
                              bgcolor: getInsightColor(insight.category, insight.type),
                              filter: 'brightness(0.9)'
                            }
                          }}
                        >
                          {insight.actionText}
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default VerifierSmartAssistant;
